import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { watchJobs, releaseJobToPool, cancelJob, ownerConfirmGuard, updateJob, healJobStatusInvariant, deriveJobStatusFix, closeBilledActiveJobs, closePastDaiJobs, syncBrokerFlagsToActiveJobs, getBrokers, jobDisplayNumber } from "../../services/firestore";
import { todayYmd } from "../../services/billingReadiness";
import { uploadGuardPhoto } from "../../services/storage";
import "./OwnerLayout.css";
import JobCreateModal from "../../components/owner/JobCreateModal";
import OrionReplyModal from "../../components/owner/OrionReplyModal";
import FerrariBulkUploadModal from "../../components/owner/FerrariBulkUploadModal";
import JobDetailModal from "../../components/owner/JobDetailModal";
import JobCalendar from "../../components/owner/JobCalendar";
import { downloadJobListPdf } from "../../services/jobListPdf";
import { downloadJobListXlsx } from "../../services/jobListXlsx";

const STATUS_LABELS = {
  intake:     { label: "Intake",      chip: "chip-amber" },
  pool:       { label: "In pool",     chip: "chip-gray" },
  grabbed:    { label: "Grabbed",     chip: "chip-blue" },
  confirmed:  { label: "Confirmed",   chip: "chip-blue" },
  inProgress: { label: "In progress", chip: "chip-teal" },
  complete:   { label: "Complete",    chip: "chip-green" },
  cancelled:  { label: "Cancelled",   chip: "chip-red" },
};

const JOB_TYPE_LABELS = { inbound: "Inbound", outbound: "Outbound", roundtrip: "Round trip", direct: "Direct" };

// "John Doe" -> "John D."; "John" -> "John"; "" -> "Guard"
function shortName(full) {
  if (!full) return "Guard";
  const parts = String(full).trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

// Format scheduledDate "2026-04-22" as local "Apr 22"; fall back to createdAt.
function formatScheduledShort(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function JobInbox() {
  const { userProfile } = useAuth();
  const [jobs, setJobs] = useState([]);
  // Broker rate cards — the source of truth for each broker's dispatcher
  // (jobs snapshot dispatcherName at creation, which goes stale when a
  // rate card is corrected later, e.g. Kallati's Orion → Executive).
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulkPods, setShowBulkPods] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [filter, setFilter] = useState("active");
  // Tracks whether the window is too narrow for the roomy column widths.
  const [narrowList, setNarrowList] = useState(
    typeof window !== "undefined" && window.innerWidth < 1200
  );
  // Phone width: cards instead of the table (see PhoneJobList).
  const [isPhone, setIsPhone] = useState(
    typeof window !== "undefined" && window.innerWidth < PHONE_WIDTH
  );
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  useEffect(() => {
    const onResize = () => {
      setNarrowList(window.innerWidth < 1200);
      setIsPhone(window.innerWidth < PHONE_WIDTH);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // List view (table) is the default; Calendar view shows the month grid for
  // owners who want to scan / print upcoming directs.
  const [view, setView] = useState("list");
  // Shared list-view filters (also surfaced on the calendar via its own toolbar).
  const [brokerFilter, setBrokerFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [repFilter, setRepFilter] = useState("all");
  // Period: "all" | "thisMonth" | "nextMonth" — filters by job.scheduledDate.
  const [periodFilter, setPeriodFilter] = useState("all");
  // Click-to-sort. Default: scheduledDate ascending.
  const [sortKey, setSortKey] = useState("scheduledDate");
  const [sortDir, setSortDir] = useState("asc");
  // Free-text search across job number, broker, dispatcher, rep, and any
  // stop's location/address. Works in combo with the other filters.
  const [searchQuery, setSearchQuery] = useState("");
  // "Did this job happen?" emails from Orion — build the answer in their format.
  const [orionReply, setOrionReply] = useState(false);
  // When set (bundleId string), the inbox shows only jobs sharing that
  // bundleId. Triggered by clicking a 📎 BUNDLE chip on any row. Cleared
  // by the dismiss button on the bundle banner.
  const [bundleFilter, setBundleFilter] = useState(null);
  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }
  const [releasedJobs, setReleasedJobs] = useState([]);
  const [grabbedJobs, setGrabbedJobs] = useState([]);
  const [toast, setToast] = useState("");
  const seenGrabbedIds = useRef(new Set());
  const notifPermission = useRef(null);
  // Track which jobs we've already auto-healed this session so a noisy
  // watcher (re-firing on every minor write) doesn't spam updateDoc calls.
  const healedJobIds = useRef(new Set());

  // Request browser notification permission once
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then((p) => { notifPermission.current = p; });
    } else if ("Notification" in window) {
      notifPermission.current = Notification.permission;
    }
  }, []);

  // One-time cleanup: close out already-billed DAI directs that are
  // still stuck in an active workflow status so they stop cluttering
  // the active inbox. Idempotent; runs once per mount. Non-blocking —
  // the watchJobs listener picks up the status changes automatically.
  useEffect(() => {
    if (!userProfile?.companyId) return;
    getBrokers(userProfile.companyId).then(setBrokers);
  }, [userProfile?.companyId]);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    closeBilledActiveJobs(userProfile.companyId)
      .then((n) => { if (n > 0) console.log(`Closed ${n} already-billed active job(s).`); })
      .catch((err) => console.warn("closeBilledActiveJobs failed", err));
    // DAI directs whose date has strictly passed get marked complete so
    // they drop off the active inbox — they bill as forwarded batches
    // and are rarely worked in the guard app, so nothing else advances
    // them. Same "✅ past" rule as the Bundled tab rows.
    closePastDaiJobs(userProfile.companyId, todayYmd())
      .then((n) => { if (n > 0) console.log(`Closed ${n} past-date DAI job(s).`); })
      .catch((err) => console.warn("closePastDaiJobs failed", err));
    // Copy broker rate-card flags (requiresRepConfirm, OOT follow rule)
    // onto active jobs that are missing them — parsed jobs never
    // inherited these at creation. Idempotent; watcher picks up writes.
    syncBrokerFlagsToActiveJobs(userProfile.companyId)
      .then((n) => { if (n > 0) console.log(`Synced broker flags onto ${n} active job(s).`); })
      .catch((err) => console.warn("syncBrokerFlagsToActiveJobs failed", err));
  }, [userProfile?.companyId]);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    const statusFilter =
      filter === "active"   ? ["intake","pool","grabbed","confirmed","inProgress"] :
      filter === "complete" ? ["complete","cancelled"] : [];
    const unsub = watchJobs(userProfile.companyId, statusFilter, (data) => {
      setJobs(data);
      setLoading(false);
      setReleasedJobs(data.filter((j) => j.guardReleased && j.status === "pool"));

      // Self-heal status/assignment invariant violations. These show up as
      // "0 assigned + Confirmed" or "1 assigned + In pool" rows in the
      // inbox (legacy records from before reassignGuard auto-reverted
      // status). Fire-and-forget — the next snapshot will reflect the fix.
      data.forEach((job) => {
        if (healedJobIds.current.has(job.id)) return;
        if (deriveJobStatusFix(job)) {
          healedJobIds.current.add(job.id);
          healJobStatusInvariant(job).catch((err) => {
            console.warn("Couldn't heal job status invariant", job.id, err);
            // Allow retry next snapshot if it failed (e.g. transient).
            healedJobIds.current.delete(job.id);
          });
        }
      });

      // Detect newly grabbed jobs and fire browser notification
      const grabbed = data.filter((j) => j.status === "grabbed");
      setGrabbedJobs(grabbed);
      grabbed.forEach((job) => {
        if (!seenGrabbedIds.current.has(job.id)) {
          seenGrabbedIds.current.add(job.id);
          // Only fire if this isn't the very first load (set size > grabbed.length means we had prior data)
          if (seenGrabbedIds.current.size > grabbed.length || seenGrabbedIds.current.size > 1) {
            if ("Notification" in window && Notification.permission === "granted" && document.hidden) {
              new Notification("Guard grabbed a job", {
                body: `${job.jobNumber} — ${job.brokerName || ""}. Tap to confirm.`,
                icon: "/logo192.png",
                tag: job.id,
              });
            }
          }
        }
      });
      // Clean up IDs that are no longer grabbed (confirmed / cancelled)
      const grabbedSet = new Set(grabbed.map((j) => j.id));
      seenGrabbedIds.current.forEach((id) => {
        if (!grabbedSet.has(id)) seenGrabbedIds.current.delete(id);
      });
    });
    return unsub;
  }, [userProfile, filter]);

  async function handleRelease(jobId) {
    await releaseJobToPool(jobId);
  }

  async function handleCancel(jobId) {
    if (window.confirm("Cancel this job?")) await cancelJob(jobId, "Cancelled by owner");
  }

  async function handleConfirm(jobId) {
    await ownerConfirmGuard(jobId);
  }

  async function handleReassign(job) {
    await updateJob(job.id, { guardReleased: false, status: "intake" });
  }

  // Ferrari/Executive jobs bill off the paper waybill, not a guard
  // workflow — the owner often just needs to push one from the inbox
  // straight onto Ready to bill (mark complete), attaching the waybill
  // photo as the POD on the way.
  function isFerrariStyleJob(j) {
    return ["ferrari", "executive"].some((k) =>
      (j.brokerName || "").toLowerCase().includes(k)
      || (rateCardDispatcher(j) || "").toLowerCase().includes(k));
  }

  async function handleReadyToBill(job) {
    if (!window.confirm(`Move ${job.jobNumber || "this job"} to Ready to bill? It will be marked complete.`)) return;
    try {
      await updateJob(job.id, { status: "complete", completedAt: new Date().toISOString() });
      setToast(`${job.jobNumber || "Job"} moved to Ready to bill`);
      setTimeout(() => setToast(""), 3500);
    } catch (err) {
      console.error("Couldn't move job to Ready to bill", err);
      alert("Couldn't move to Ready to bill: " + (err?.message || err));
    }
  }

  // Drop an image (waybill photo/screenshot) onto a Ferrari/Executive row
  // to save it as the job's package document (POD) — stamped on every
  // package and real stop that doesn't already have one, same as the
  // create-modal flow.
  async function handlePodDrop(job, files) {
    const list = Array.from(files || []);
    if (list.length === 0) {
      alert(
        "That drag only carried a link to the attachment. Save the file first " +
        "(desktop or the Downloads bar), then drag the file onto the job."
      );
      return;
    }
    const img = list.find((f) => f.type?.startsWith("image/"));
    if (!img) {
      alert("Only images can be saved as the POD — drop a photo or screenshot of the waybill (not a PDF).");
      return;
    }
    try {
      const url = await uploadGuardPhoto(img, job.id, "package-doc", userProfile?.id || "owner", "pod");
      const patch = {
        stops: (job.stops || []).map((s) =>
          s.type === "airport" ? s : { ...s, podPhotoUrl: s.podPhotoUrl || url }),
      };
      if ((job.packages || []).length > 0) {
        patch.packages = job.packages.map((p) => ({ ...p, podPhotoUrl: p.podPhotoUrl || url }));
      }
      await updateJob(job.id, patch);
      setToast(`POD saved to ${job.jobNumber || "job"}`);
      setTimeout(() => setToast(""), 3500);
    } catch (err) {
      console.error("POD drop failed", err);
      alert("Couldn't save the POD: " + (err?.message || err));
    }
  }

  async function handleLeaveInPool(job) {
    await updateJob(job.id, { guardReleased: false });
  }

  function formatDate(ts) {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  // Compute the [start, end] date range for the active period filter.
  const periodRange = (() => {
    if (periodFilter === "all") return null;
    const now = new Date();
    const offset = periodFilter === "nextMonth" ? 1 : 0;
    const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0); // last day
    const ymd = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { start: ymd(start), end: ymd(end) };
  })();

  // Sort comparator — keyed on whichever column the user clicked. Strings
  // missing on a job sort to the bottom so they're easy to spot and fix.
  function compare(a, b) {
    const dir = sortDir === "asc" ? 1 : -1;
    const lc = (s) => String(s || "").toLowerCase();
    let av, bv;
    switch (sortKey) {
      case "jobNumber": av = lc(a.jobNumber || "~"); bv = lc(b.jobNumber || "~"); break;
      case "jobType":   av = lc(a.jobType   || "~"); bv = lc(b.jobType   || "~"); break;
      case "broker":    av = lc(a.brokerName|| "~"); bv = lc(b.brokerName|| "~"); break;
      case "guards":    av = (a.guardAssignments?.length || 0); bv = (b.guardAssignments?.length || 0); break;
      case "route":     av = lc((a.stops || []).filter((s) => s.type !== "airport").map((s) => s.outOfTown ? "OOT" : s.locationName).join(" ")) || "~";
                        bv = lc((b.stops || []).filter((s) => s.type !== "airport").map((s) => s.outOfTown ? "OOT" : s.locationName).join(" ")) || "~"; break;
      case "status":    av = lc(a.status || "~"); bv = lc(b.status || "~"); break;
      case "scheduledDate":
      default: {
        // Date fallthrough — compare date, then job type (inbound before
        // outbound, since inbound runs in the morning and outbound in the
        // evening), then time, all as strings.
        const da = a.scheduledDate || "9999-99-99";
        const db = b.scheduledDate || "9999-99-99";
        if (da !== db) return da.localeCompare(db) * dir;
        const typeRank = (t) => (t === "inbound" ? 0 : t === "outbound" ? 1 : 2);
        const ra = typeRank(a.jobType), rb = typeRank(b.jobType);
        if (ra !== rb) return (ra - rb) * dir;
        return ((a.scheduledTime || "99:99").localeCompare(b.scheduledTime || "99:99")) * dir;
      }
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return  1 * dir;
    return 0;
  }

  // Dispatcher for a job, rate card first: the broker doc's dispatcherName
  // (matched by broker name, same loose matching as Ready to bill) wins
  // over the value snapshotted on the job at creation, which can be stale.
  function rateCardDispatcher(j) {
    const wanted = (j.brokerName || "").toLowerCase();
    if (!wanted) return j.dispatcherName || "";
    const broker = brokers.find((b) => (b.name || "").toLowerCase() === wanted)
      || brokers.find((b) => {
        const bn = (b.name || "").toLowerCase();
        return bn && (wanted.includes(bn) || bn.includes(wanted));
      });
    return broker?.dispatcherName || j.dispatcherName || "";
  }

  // Build a matchable haystack of every searchable field on a job. Pre-built
  // once per job so the search filter stays cheap as the user types.
  function searchHaystack(j) {
    const stopText = (j.stops || [])
      .map((s) => `${s.locationName || ""} ${s.address || ""}`)
      .join(" ");
    // HAWB / tracking numbers from every package on the job. When
    // Malca calls about a specific HAWB, you can paste that number
    // straight into the search box and find the job + click into
    // the POD photos. Critical for after-the-fact package tracking.
    const hawbText = (j.packages || [])
      .map((p) => p.hawbNumber || "")
      .filter(Boolean)
      .join(" ");
    return [
      j.jobNumber,
      j.brokerName,
      rateCardDispatcher(j),
      j.salesRepName,
      j.salesRepPhone,
      stopText,
      hawbText,
      j.notes,
    ].filter(Boolean).join(" ").toLowerCase();
  }
  const trimmedQuery = searchQuery.trim().toLowerCase();

  // Apply broker / type / rep / period / search / bundle filters before
  // partitioning. The bundle filter trumps the others — when an owner
  // clicks a bundle chip they want to see THAT bundle, full stop, not
  // a bundle further filtered by their previous broker/period choices.
  const filteredJobs = jobs.filter((j) => {
    if (bundleFilter && j.bundleId !== bundleFilter) return false;
    if (bundleFilter) return true; // skip remaining filters when scoped to a bundle
    if (brokerFilter !== "all" && j.brokerName !== brokerFilter) return false;
    if (typeFilter !== "all" && j.jobType !== typeFilter) return false;
    if (repFilter !== "all" && j.salesRepName !== repFilter) return false;
    if (periodRange) {
      if (!j.scheduledDate) return false;
      if (j.scheduledDate < periodRange.start || j.scheduledDate > periodRange.end) return false;
    }
    if (trimmedQuery && !searchHaystack(j).includes(trimmedQuery)) return false;
    return true;
  });

  // Distinct values across ALL loaded jobs (so the dropdowns don't shrink
  // away as filters are applied).
  const brokerOptions = Array.from(new Set(jobs.map((j) => j.brokerName).filter(Boolean))).sort();
  const repOptions = Array.from(new Set(jobs.map((j) => j.salesRepName).filter(Boolean))).sort();

  // Group jobs: parents first, children indented; both sorted by scheduledDate.
  // A child is only ever drawn underneath its parent, so one whose parent is
  // missing from THIS view would never render at all — it would look like the
  // job was lost. That happens routinely now: a vault delivery is created off
  // a job being billed, so the child is Intake while the parent is already
  // Complete and filtered out of the Active tab. Promote those to top level.
  const topLevelJobs = filteredJobs.filter((j) => !j.parentJobId);
  const visibleParentIds = new Set(topLevelJobs.map((j) => j.id));
  const orphanedChildren = filteredJobs.filter(
    (j) => j.parentJobId && !visibleParentIds.has(j.parentJobId)
  );
  const parentJobs = [...topLevelJobs, ...orphanedChildren].sort(compare);
  const childMap = {};
  filteredJobs
    .filter((j) => j.parentJobId && visibleParentIds.has(j.parentJobId))
    .forEach((j) => {
      if (!childMap[j.parentJobId]) childMap[j.parentJobId] = [];
      childMap[j.parentJobId].push(j);
    });
  Object.values(childMap).forEach((arr) => arr.sort(compare));

  // Build a bundle-summary index: bundleId → { count, dateRange } so each
  // row can show "Bundle · 5 jobs · May 9–14" without re-iterating the
  // full list. Skip bundles of size 1 — a "bundle of one" is just a job.
  // We use the unfiltered `jobs` so a row keeps its accurate sibling
  // count even when the user has filtered the view down to one date.
  const bundleSummary = (() => {
    const byBundle = new Map();
    for (const j of jobs) {
      if (!j.bundleId) continue;
      const cur = byBundle.get(j.bundleId) || { count: 0, dates: [] };
      cur.count += 1;
      if (j.scheduledDate) cur.dates.push(j.scheduledDate);
      byBundle.set(j.bundleId, cur);
    }
    const out = {};
    for (const [bid, info] of byBundle.entries()) {
      if (info.count < 2) continue;
      const sorted = info.dates.slice().sort();
      out[bid] = {
        count: info.count,
        firstDate: sorted[0] || null,
        lastDate: sorted[sorted.length - 1] || null,
      };
    }
    return out;
  })();

  // Columns: Job # · Type · Broker/Dispatcher/Rep · Guards · Route · Scheduled · Status · Actions
  // The "Route" column replaces the old "Airport" column — owners only run
  // out of one hub so airport code adds no info, but a quick read of the
  // stop names ("Elemetal · Diamonds Direct") lets you ID a job at a glance.
  // In a half-width window the fixed columns ate everything: 650px of them
  // left Route about 97px, truncated to "ELEMETAL ...", while Guards kept a
  // full 170px to say "0/1 assigned". Below this width every fixed column
  // gives back what it can spare and Route takes the slack - the stop names
  // are the fastest way to identify a job at a glance.
  const COL = narrowList
    ? "78px 56px 0.8fr 92px 1.7fr 84px 68px 84px"
    : "100px 70px 1fr 170px 1.4fr 100px 90px 120px";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Job inbox</div>
          <div className="page-subtitle">
            {jobs.length} job{jobs.length !== 1 ? "s" : ""} · {filter === "active" ? "Active" : filter === "complete" ? "Completed" : "All"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* List / Calendar view toggle */}
          <div style={{ display: "flex", gap: 0, border: "1px solid #ddd", borderRadius: 7, overflow: "hidden" }}>
            {[
              { key: "list", label: "📋 List" },
              { key: "calendar", label: "🗓 Calendar" },
            ].map((v) => (
              <button key={v.key}
                onClick={() => setView(v.key)}
                style={{
                  padding: "6px 12px", fontSize: 12, fontWeight: 600,
                  background: view === v.key ? "#185FA5" : "#fff",
                  color: view === v.key ? "#fff" : "#555",
                  border: "none", cursor: "pointer",
                }}
              >{v.label}</button>
            ))}
          </div>
          {isPhone ? (
            // Phone: Bulk upload moves into a ⋯ menu; New job is the round +.
            <div style={{ position: "relative" }}>
              <button className="btn" aria-label="More" onClick={() => setHeaderMenuOpen((o) => !o)}>⋯</button>
              {headerMenuOpen && (
                <div style={{
                  position: "absolute", right: 0, top: "110%", zIndex: 30, background: "#fff",
                  border: "1px solid #dde2e8", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.15)",
                  minWidth: 220, fontSize: 14, overflow: "hidden",
                }}>
                  <div style={{ padding: "12px 14px", cursor: "pointer" }}
                    onClick={() => { setHeaderMenuOpen(false); setShowBulkPods(true); }}>➕ Bulk-upload Ferrari PODs</div>
                </div>
              )}
            </div>
          ) : (
            <>
              <button className="btn" onClick={() => setShowBulkPods(true)}
                title="Upload a batch of Ferrari POD/HAWB form photos — each parses on its own, then you group them into jobs">
                ➕ Bulk-upload Ferrari PODs
              </button>
              <button className="btn btn-blue" onClick={() => setShowCreate(true)}>+ New job</button>
            </>
          )}
        </div>
      </div>
      {isPhone && (
        <button className="btn-blue" aria-label="New job" onClick={() => setShowCreate(true)} style={{
          position: "fixed", right: 18, bottom: 22, zIndex: 40, width: 56, height: 56, borderRadius: "50%",
          border: "none", fontSize: 30, lineHeight: 1, boxShadow: "0 6px 16px rgba(24,95,165,.35)", cursor: "pointer",
        }}>+</button>
      )}

      {/* Guard-grabbed alerts — require confirmation */}
      {grabbedJobs.map((job) => {
        const guardName = job.guardAssignments?.find((g) => g.guardId === job.grabbedByGuardId)?.guardName
          || job.guardAssignments?.[0]?.guardName
          || "A guard";
        // Friendly date: "Thu May 28" if we have a scheduledDate; fall
        // back to whatever's on the doc. Stops summary mirrors the
        // Ready-to-Bill / Statements convention so the owner sees the
        // same "Origin → Destination" shape everywhere.
        const dateLabel = (() => {
          if (!job.scheduledDate) return "";
          const [y, m, d] = String(job.scheduledDate).split("-").map(Number);
          if (!y || !m || !d) return job.scheduledDate;
          return new Date(y, m - 1, d).toLocaleDateString("en-US", {
            weekday: "short", month: "short", day: "numeric",
          });
        })();
        const timeLabel = job.scheduledTime || "";
        const realStops = (job.stops || []).filter((s) => s.type !== "airport");
        const routeSummary = realStops
          .map((s) => s.outOfTown ? "OUT OF TOWN" : (s.locationName || "—"))
          .join(" → ");
        const jobNumberDisplay = jobDisplayNumber(job)
          || job.brokerName
          || "Job";
        const typeLabel = job.jobType
          ? job.jobType.charAt(0).toUpperCase() + job.jobType.slice(1)
          : "";
        return (
          <div key={job.id} style={{
            background: "#e8f0fe", borderLeft: "4px solid #185FA5",
            margin: "10px 20px 0", padding: "10px 14px", borderRadius: "0 8px 8px 0",
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <span style={{ fontSize: 18 }}>🙋</span>
            <div style={{ flex: 1, fontSize: 12, minWidth: 0 }}>
              <div style={{ marginBottom: 3 }}>
                <strong>{guardName}</strong> grabbed <strong>{jobNumberDisplay}</strong>. Confirm or reassign.
              </div>
              <div style={{ fontSize: 11, color: "#555", display: "flex", flexWrap: "wrap", gap: 6 }}>
                {dateLabel && (
                  <span><strong>📅 {dateLabel}</strong>{timeLabel ? ` ${timeLabel}` : ""}</span>
                )}
                {typeLabel && (
                  <span style={{ color: "#888" }}>· {typeLabel}</span>
                )}
                {job.brokerName && (
                  <span style={{ color: "#888" }}>· {job.brokerName}</span>
                )}
                {routeSummary && (
                  <span style={{
                    color: "#185FA5", fontWeight: 500,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    minWidth: 0, maxWidth: "100%",
                  }}>· {routeSummary}</span>
                )}
              </div>
            </div>
            <button className="btn btn-sm btn-blue" onClick={() => handleConfirm(job.id)}>
              Confirm guard
            </button>
            <button className="btn btn-sm" onClick={() => setSelectedJobId(job.id)}>
              View job
            </button>
          </div>
        );
      })}

      {/* Guard-released alerts — require action, not dismissable */}
      {releasedJobs.map((job) => (
        <div key={job.id} style={{
          background: "#fff3cd", borderLeft: "4px solid #EF9F27",
          margin: "10px 20px 0", padding: "10px 14px", borderRadius: "0 8px 8px 0",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div style={{ flex: 1, fontSize: 12 }}>
            <strong>Guard released {job.jobNumber}</strong> back to the pool.
            It's available for any guard to grab — dismiss this alert when you've seen it,
            or pull it out of the pool to assign someone manually.
          </div>
          <button
            className="btn btn-sm btn-blue"
            onClick={() => handleLeaveInPool(job)}
            title="Keep the job in the pool so any available guard can grab it"
          >
            Keep in pool
          </button>
          <button
            className="btn btn-sm"
            onClick={() => handleReassign(job)}
            title="Pull the job out of the pool so you can hand-pick a guard"
          >
            Assign manually
          </button>
        </div>
      ))}

      <div className="page-body">
        {view === "list" && (
          <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
            {/* Answer an Orion "did this run?" email in their own format. */}
            <button className="btn btn-sm" onClick={() => setOrionReply(true)}
              title="Build a reply table for jobs Orion asked about">
              📋 Reply to Orion
            </button>
            {/* Search box — leftmost so it's the first thing the eye lands on */}
            <div style={{ position: "relative" }}>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search job #, HAWB, broker, rep, location…"
                style={{
                  width: 260, padding: "6px 28px 6px 30px",
                  border: "1px solid #ddd", borderRadius: 7, fontSize: 12,
                }}
              />
              <span style={{
                position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
                fontSize: 12, color: "#aaa", pointerEvents: "none",
              }}>🔍</span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  title="Clear search"
                  style={{
                    position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", color: "#888", fontSize: 14,
                    cursor: "pointer", padding: 0, width: 18, height: 18,
                  }}
                >✕</button>
              )}
            </div>

            {/* Status chips */}
            <div style={{ display: "flex", gap: 6 }}>
              {["active","complete","all"].map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`btn btn-sm ${filter === f ? "btn-blue" : ""}`}>
                  {f === "active" ? "Active" : f === "complete" ? "Completed" : "All jobs"}
                </button>
              ))}
            </div>

            <span style={{ width: 1, height: 22, background: "#ddd" }} />

            {/* Period chips */}
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { k: "all",        label: "Any date"    },
                { k: "thisMonth",  label: "This month"  },
                { k: "nextMonth",  label: "Next month"  },
              ].map((p) => (
                <button key={p.k} onClick={() => setPeriodFilter(p.k)}
                  className={`btn btn-sm ${periodFilter === p.k ? "btn-blue" : ""}`}>
                  {p.label}
                </button>
              ))}
            </div>

            <span style={{ width: 1, height: 22, background: "#ddd" }} />

            {/* Cross-cutting filters — same as calendar toolbar */}
            <label style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "center", gap: 5 }}>
              Broker
              <select value={brokerFilter} onChange={(e) => setBrokerFilter(e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
                <option value="all">All</option>
                {brokerOptions.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "center", gap: 5 }}>
              Type
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
                <option value="all">All</option>
                <option value="direct">Direct</option>
                <option value="outbound">Outbound</option>
                <option value="inbound">Inbound</option>
                <option value="roundtrip">Round trip</option>
              </select>
            </label>
            {repOptions.length > 0 && (
              <label style={{ fontSize: 11, color: "#666", display: "flex", alignItems: "center", gap: 5 }}>
                Rep
                <select value={repFilter} onChange={(e) => setRepFilter(e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
                  <option value="all">All</option>
                  {repOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
            )}

            {(brokerFilter !== "all" || typeFilter !== "all" || repFilter !== "all" || periodFilter !== "all" || searchQuery) && (
              <button
                className="btn btn-sm"
                onClick={() => {
                  setBrokerFilter("all");
                  setTypeFilter("all");
                  setRepFilter("all");
                  setPeriodFilter("all");
                  setSearchQuery("");
                }}
                style={{ fontSize: 10 }}
              >Clear filters</button>
            )}

            <span style={{ marginLeft: "auto", fontSize: 11, color: "#666" }}>
              Showing {parentJobs.length} of {jobs.length}
            </span>
            {(() => {
              // Filter summary + filename helpers used by both exports so
              // the PDF and XLSX carry the same label / date range.
              const parts = [];
              if (filter !== "all")        parts.push(`Status: ${filter}`);
              if (brokerFilter !== "all")  parts.push(`Broker: ${brokerFilter}`);
              if (typeFilter !== "all")    parts.push(`Type: ${JOB_TYPE_LABELS[typeFilter] || typeFilter}`);
              if (repFilter !== "all")     parts.push(`Rep: ${repFilter}`);
              const periodLabel = periodFilter === "thisMonth" ? "This Month "
                : periodFilter === "nextMonth" ? "Next Month "
                : "";
              const filterLine = parts.join(" · ");
              const typeSlug = typeFilter !== "all" ? `-${typeFilter}` : "";
              const periodSlug = periodFilter === "thisMonth" ? "-thismonth"
                : periodFilter === "nextMonth" ? "-nextmonth" : "";
              const baseName = `job-list${typeSlug}${periodSlug}`;
              const title = `${periodLabel}Job Schedule`;
              return (
                <>
                  <button
                    className="btn btn-sm"
                    onClick={async () => {
                      try {
                        await downloadJobListPdf(parentJobs, childMap, {
                          title, filterLine, filename: `${baseName}.pdf`,
                        });
                      } catch (err) {
                        alert("Couldn't build PDF: " + (err?.message || err));
                      }
                    }}
                    title="Download as PDF (mobile-friendly print)"
                  >📄 PDF</button>
                  <button
                    className="btn btn-sm"
                    onClick={() => {
                      try {
                        downloadJobListXlsx(parentJobs, childMap, {
                          filename: `${baseName}.xlsx`,
                          sheetName: title,
                        });
                      } catch (err) {
                        alert("Couldn't build spreadsheet: " + (err?.message || err));
                      }
                    }}
                    title="Download as Excel spreadsheet to email guards"
                  >📊 Excel</button>
                  <button
                    className="btn btn-sm btn-blue"
                    onClick={() => window.print()}
                    title="Print this filtered list (desktop browsers only)"
                  >🖨 Print</button>
                </>
              );
            })()}
          </div>
        )}

        {/* Bundle-filter banner. Tells the owner they're scoped to a
            bundle (other filters are temporarily ignored) and gives them
            a one-click escape hatch back to the full list. */}
        {bundleFilter && (
          <div className="no-print" style={{
            margin: "0 0 12px",
            background: "#fffbe9", border: "1px solid #f0d878",
            borderRadius: 8, padding: "8px 12px",
            display: "flex", alignItems: "center", gap: 10, fontSize: 12,
          }}>
            <span style={{ fontSize: 14 }}>📎</span>
            <span style={{ flex: 1, color: "#7a5400" }}>
              Showing one bundle (<strong>{bundleSummary[bundleFilter]?.count || parentJobs.length}</strong> jobs).
              Other filters are paused while you're scoped to this bundle.
            </span>
            <button
              className="btn btn-sm"
              onClick={() => setBundleFilter(null)}
              style={{ fontSize: 11 }}
            >
              ✕ Clear bundle filter
            </button>
          </div>
        )}

        {loading ? (
          <div className="loading-state">Loading jobs...</div>
        ) : view === "calendar" ? (
          <JobCalendar
            jobs={jobs}
            onSelectJob={setSelectedJobId}
            brokerFilter={brokerFilter}
            setBrokerFilter={setBrokerFilter}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            repFilter={repFilter}
            setRepFilter={setRepFilter}
            periodFilter={periodFilter}
          />
        ) : jobs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div>No jobs yet</div>
            <button className="btn btn-blue" onClick={() => setShowCreate(true)}>Create your first job</button>
          </div>
        ) : (
          <div className="job-list-print-area">
            {/* Print stylesheet — hides nav, sidebar, alerts, page header
                buttons; keeps only the table + a printable header showing the
                applied filters so the printout is self-explanatory. */}
            <style>{`
              @media print {
                body * { visibility: hidden; }
                .job-list-print-area, .job-list-print-area * { visibility: visible; }
                .job-list-print-area { position: absolute; left: 0; top: 0; width: 100%; }
                .job-list-print-area .no-print { display: none !important; }
                .job-list-print-area .print-actions-col { display: none !important; }
                /* Hide the rich screen layout in favor of the simplified
                   print table below. The screen layout has columns owners
                   need on the dashboard (job#, status chip, action buttons)
                   that are noise on a printed schedule. */
                .job-list-print-area .screen-only { display: none !important; }
                .job-list-print-area .print-table { display: block !important; }
                .job-list-print-area .print-row { page-break-inside: avoid; }
              }
              @media screen {
                .job-list-print-area .print-only,
                .job-list-print-area .print-table { display: none; }
              }
            `}</style>

            {/* Print-only header */}
            <div className="print-only" style={{ marginBottom: 10, padding: "0 4px" }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {periodFilter === "thisMonth" ? "This Month "
                  : periodFilter === "nextMonth" ? "Next Month "
                  : ""}Job Schedule
              </div>
              <div style={{ fontSize: 11, color: "#444", marginTop: 4 }}>
                {filter !== "all" && <span>Status: {filter} · </span>}
                {brokerFilter !== "all" && <span>Broker: {brokerFilter} · </span>}
                {typeFilter !== "all" && <span>Type: {JOB_TYPE_LABELS[typeFilter] || typeFilter} · </span>}
                {repFilter !== "all" && <span>Rep: {repFilter} · </span>}
                {parentJobs.length} job{parentJobs.length !== 1 ? "s" : ""}
                <span style={{ marginLeft: 8, color: "#888" }}>
                  Generated {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
            </div>

          {isPhone ? (
            <PhoneJobList
              jobs={parentJobs.flatMap((j) => [j, ...(childMap[j.id] || [])])}
              onSelect={setSelectedJobId} onRelease={handleRelease}
              onCancel={handleCancel} onConfirm={handleConfirm}
              onReadyToBill={(j) => (isFerrariStyleJob(j) ? handleReadyToBill : null)}
              onPodDrop={(j) => (isFerrariStyleJob(j) ? handlePodDrop : null)}
              dispatcherFor={rateCardDispatcher} />
          ) : (
          <div className="panel screen-only">
            {/* Header */}
            <div style={{
              display: "grid", gridTemplateColumns: COL,
              padding: "8px 14px", background: "#f8f8f8",
              borderBottom: "1px solid #e8e8e8",
              fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
            }}>
              {(() => {
                // Reusable sortable header. Click toggles direction; arrow
                // shows on the active column.
                const HeaderCell = ({ label, k, align }) => {
                  const active = sortKey === k;
                  const arrow = active ? (sortDir === "asc" ? " ↑" : " ↓") : "";
                  return (
                    <span
                      onClick={() => handleSort(k)}
                      style={{
                        cursor: "pointer", userSelect: "none",
                        color: active ? "#185FA5" : "#888",
                        textAlign: align || "left",
                      }}
                      title={`Sort by ${label}`}
                    >
                      {label}{arrow}
                    </span>
                  );
                };
                return (
                  <>
                    <HeaderCell label="Job #" k="jobNumber" />
                    <HeaderCell label="Type" k="jobType" />
                    <HeaderCell label="Broker · Dispatcher" k="broker" />
                    <HeaderCell label="Guards" k="guards" />
                    <HeaderCell label="Route" k="route" />
                    <HeaderCell label="Scheduled" k="scheduledDate" />
                    <HeaderCell label="Status" k="status" />
                    <span className="print-actions-col" style={{ textAlign: "right" }}>Actions</span>
                  </>
                );
              })()}
            </div>

            {parentJobs.map((job) => {
              const children = childMap[job.id] || [];
              return (
                <React.Fragment key={job.id}>
                  <JobRow job={job} COL={COL} onRelease={handleRelease}
                    onCancel={handleCancel} onConfirm={handleConfirm}
                    onSelect={setSelectedJobId}
                    dispatcherName={rateCardDispatcher(job)}
                    onReadyToBill={isFerrariStyleJob(job) ? handleReadyToBill : null}
                    onPodDrop={isFerrariStyleJob(job) ? handlePodDrop : null}
                    formatDate={formatDate} isParent={children.length > 0}
                    bundleInfo={bundleSummary[job.bundleId]}
                    onBundleClick={setBundleFilter} />
                  {children.map((child) => (
                    <JobRow key={child.id} job={child} COL={COL}
                      onRelease={handleRelease} onCancel={handleCancel}
                      onConfirm={handleConfirm} onSelect={setSelectedJobId}
                      dispatcherName={rateCardDispatcher(child)}
                      onReadyToBill={isFerrariStyleJob(child) ? handleReadyToBill : null}
                      onPodDrop={isFerrariStyleJob(child) ? handlePodDrop : null}
                      formatDate={formatDate} isChild
                      bundleInfo={bundleSummary[child.bundleId]}
                      onBundleClick={setBundleFilter} />
                  ))}
                </React.Fragment>
              );
            })}
          </div>
          )}

          {/* ── Print-only simplified table ──────────────────────────
              Owner asked for a clean printout with just date, time,
              broker, account rep, and route. Screen-only chrome (job
              numbers, status chips, action buttons, guard slots) is
              omitted because none of it is useful on paper.
              The table below renders the SAME filtered + sorted job
              list as the screen view — just with different columns.
              When printing a "Direct" filter applied, this becomes the
              direct-only schedule sheet. */}
          <div className="print-table">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #444", textAlign: "left" }}>
                  <th style={{ padding: "6px 8px", width: 90 }}>Date</th>
                  <th style={{ padding: "6px 8px", width: 60 }}>Time</th>
                  <th style={{ padding: "6px 8px", width: "30%" }}>Broker · Rep</th>
                  <th style={{ padding: "6px 8px" }}>Route</th>
                </tr>
              </thead>
              <tbody>
                {parentJobs.flatMap((job) => {
                  // Render parent + any children inline so multi-detail
                  // bundles (DAI) print as separate dated rows, not one
                  // collapsed parent. Sort children by their own dates.
                  const rows = [job, ...((childMap[job.id] || []).slice().sort((a, b) =>
                    String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || ""))
                  ))];
                  return rows.map((j) => {
                    const dateStr = formatScheduledShort(j.scheduledDate) || "—";
                    const timeStr = j.scheduledTime || "";
                    // Real stops only — exclude the airport row, which is
                    // implicit on inbound/outbound and just adds noise.
                    const realStops = (j.stops || []).filter((s) => s.type !== "airport");
                    return (
                      <tr key={j.id} className="print-row" style={{ borderBottom: "1px solid #ddd" }}>
                        <td style={{ padding: "6px 8px", verticalAlign: "top", whiteSpace: "nowrap" }}>{dateStr}</td>
                        <td style={{ padding: "6px 8px", verticalAlign: "top", whiteSpace: "nowrap" }}>{timeStr}</td>
                        <td style={{ padding: "6px 8px", verticalAlign: "top" }}>
                          <div style={{ fontWeight: 600 }}>{j.brokerName || "—"}</div>
                          {j.salesRepName && (
                            <div style={{ fontSize: 10, color: "#444", marginTop: 1 }}>
                              👤 {j.salesRepName}
                            </div>
                          )}
                          {/* Phone on its own line so it's easy to read at
                              a glance and doesn't get cut off if the rep
                              name is long. */}
                          {j.salesRepPhone && (
                            <div style={{ fontSize: 10, color: "#444" }}>
                              ☎ {j.salesRepPhone}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "6px 8px", verticalAlign: "top" }}>
                          {realStops.length === 0 ? (
                            <span>—</span>
                          ) : (
                            // Each stop on its own block — name on top,
                            // address underneath in muted color. Beats a
                            // single comma-separated line because addresses
                            // are long and would wrap unpredictably.
                            realStops.map((s, i) => (
                              <div key={i} style={{ marginBottom: i < realStops.length - 1 ? 4 : 0 }}>
                                <div style={{ fontWeight: 500 }}>
                                  {i > 0 && <span style={{ color: "#888" }}>↓ </span>}
                                  {s.outOfTown ? "OUT OF TOWN" : (s.locationName || "—")}
                                </div>
                                {!s.outOfTown && s.address && (
                                  <div style={{ fontSize: 10, color: "#666", marginLeft: i > 0 ? 12 : 0 }}>
                                    {s.address}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
          </div>
        )}
      </div>

      {showCreate && (
        <JobCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(jobNum) => {
            setShowCreate(false);
            setToast(`Job created — ${jobNum}`);
            setTimeout(() => setToast(""), 3500);
          }}
          companyId={userProfile?.companyId}
        />
      )}

      {showBulkPods && (
        <FerrariBulkUploadModal
          onClose={() => setShowBulkPods(false)}
          onCreated={(msg) => {
            setShowBulkPods(false);
            setToast(msg);
            setTimeout(() => setToast(""), 3500);
          }}
          companyId={userProfile?.companyId}
        />
      )}

      {selectedJobId && (
        <JobDetailModal
          jobId={selectedJobId}
          companyId={userProfile?.companyId}
          onClose={() => setSelectedJobId(null)}
        />
      )}

      {/* Toast notification */}
      {orionReply && (
        <OrionReplyModal
          companyId={userProfile.companyId}
          onClose={() => setOrionReply(false)}
        />
      )}

      {toast && (
        <div style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: "#111", color: "#fff", borderRadius: 8, padding: "10px 20px",
          fontSize: 13, fontWeight: 500, zIndex: 500,
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
          animation: "fadeInUp 0.2s ease",
        }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
}

function JobRow({ job, COL, onRelease, onCancel, onConfirm, onSelect, dispatcherName, onReadyToBill, onPodDrop, formatDate, isChild, isParent, bundleInfo, onBundleClick }) {
  // Use the derived status fix to render the correct chip even before the
  // heal write hits Firestore on the next snapshot. Belt-and-suspenders so
  // the UI never displays a contradictory state.
  const fix = deriveJobStatusFix(job);
  const effectiveStatus = fix?.status || job.status;
  const st = STATUS_LABELS[effectiveStatus] || { label: effectiveStatus, chip: "chip-gray" };
  // Ferrari/Executive rows accept a dropped waybill image as the POD.
  const [podDragOver, setPodDragOver] = useState(false);
  const dragHasPayload = (e) => Array.from(e.dataTransfer?.types || [])
    .some((t) => ["files", "text/uri-list", "downloadurl"].includes(String(t).toLowerCase()));
  const hasPod = (job.stops || []).some((s) => s.podPhotoUrl)
    || (job.packages || []).some((p) => p.podPhotoUrl);
  return (
    <div
      onClick={() => onSelect(job.id)}
      onDragOver={(e) => {
        if (!onPodDrop || !dragHasPayload(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setPodDragOver(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setPodDragOver(false);
      }}
      onDrop={(e) => {
        if (!onPodDrop || !dragHasPayload(e)) return;
        // preventDefault even when no file bytes arrived — otherwise the
        // browser navigates to a dropped link.
        e.preventDefault();
        e.stopPropagation();
        setPodDragOver(false);
        onPodDrop(job, e.dataTransfer?.files);
      }}
      style={{
        display: "grid", gridTemplateColumns: COL,
        padding: "10px 14px",
        paddingLeft: isChild ? 28 : 14,
        borderBottom: "1px solid #f0f0f0",
        alignItems: "center", gap: 8, fontSize: 12,
        background: isChild ? "#fafbfc" : "#fff",
        cursor: "pointer",
        // Drop-target highlight while a file is dragged over the row.
        outline: podDragOver ? "2px dashed #185FA5" : "none",
        outlineOffset: -2,
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = isChild ? "#f4f6fa" : "#fafbfc"}
      onMouseLeave={(e) => e.currentTarget.style.background = isChild ? "#fafbfc" : "#fff"}
    >
      <span style={{ fontWeight: 700, color: "#185FA5" }}>
        {/* Fall back to the first package's HAWB/order # when there's no
            broker job number — Ferrari paper forms carry their order # there
            (NYW…) rather than in jobNumber. Mirrors My Jobs + grabbed alert. */}
        {jobDisplayNumber(job) || "—"}
        {job.subJobLabel && <span style={{ marginLeft: 3, fontWeight: 400, color: "#888" }}>({job.subJobLabel})</span>}
        {isParent && <span style={{ marginLeft: 4, fontSize: 10, color: "#888" }}>▾</span>}
      </span>
      <span style={{ color: "#666" }}>{JOB_TYPE_LABELS[job.jobType] || job.jobType}</span>
      <div>
        <div style={{ fontWeight: 500 }}>{job.brokerName || "—"}</div>
        {/* Rate-card dispatcher, resolved by the parent — job.dispatcherName
            is only the creation-time snapshot and can be stale. */}
        <div style={{ fontSize: 10, color: "#888" }}>{dispatcherName || job.dispatcherName || "—"}</div>
        {job.salesRepName && (
          <div style={{ fontSize: 10, color: "#185FA5", fontWeight: 600, marginTop: 1 }}>
            👤 {job.salesRepName}
          </div>
        )}
        {/* Bundle chip — shown only when this job shares a bundleId with
            at least one other job (e.g. multi-detail DAI email). The
            tooltip surfaces the date range so the owner can verify which
            email it came from without opening every sibling. */}
        {bundleInfo && (
          <div
            // Click to filter the inbox to just this bundle's jobs.
            // stopPropagation so the row's onSelect (open detail) doesn't
            // also fire — owner just wants to scope the list.
            onClick={(e) => {
              e.stopPropagation();
              if (onBundleClick && job.bundleId) onBundleClick(job.bundleId);
            }}
            title={`Click to view all ${bundleInfo.count} jobs in this bundle · ${bundleInfo.firstDate || "?"} → ${bundleInfo.lastDate || "?"}`}
            style={{
              display: "inline-block", marginTop: 4,
              fontSize: 9, fontWeight: 700, letterSpacing: "0.3px",
              background: "#fffbe9", color: "#a36b1d",
              border: "1px solid #f0d878", borderRadius: 8,
              padding: "1px 6px", cursor: "pointer",
            }}
          >
            📎 BUNDLE · {bundleInfo.count}
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#555", lineHeight: 1.35, minWidth: 0 }}>
        {(() => {
          const required = Number(job.numGuards) || (job.guardAssignments?.length || 1);
          const assignments = job.guardAssignments || [];
          const assignedCount = assignments.length;
          const underStaffed = assignedCount < required;
          // Build slot list 1..required showing name or [open]
          const slots = [];
          for (let i = 1; i <= required; i++) {
            const a = assignments.find((g) => g.guardNumber === i);
            slots.push(a ? shortName(a.guardName) : null);
          }
          return (
            <>
              <div style={{
                fontSize: 10, fontWeight: 700,
                color: assignedCount === 0 ? "#bbb" : underStaffed ? "#b47500" : "#0a7a4a",
              }}>
                {assignedCount}/{required} assigned
              </div>
              <div style={{
                fontSize: 11, color: "#444",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {slots.map((n, i) => (
                  <span key={i}>
                    {i > 0 && <span style={{ color: "#ccc" }}> · </span>}
                    {n
                      ? <span>{n}</span>
                      : <span style={{ color: "#c9a25a", fontStyle: "italic" }}>[open]</span>}
                  </span>
                ))}
              </div>
            </>
          );
        })()}
      </div>
      {/* Route — quick read of the stops on this job. Skip the airport row
          since every job has one and it adds no info; replace OOT stops with
          the OOT chip so the owner spots those at a glance. */}
      <div style={{ minWidth: 0 }}>
      <span style={{
        display: "block",
        fontSize: 11, color: "#444",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        minWidth: 0,
      }} title={(job.stops || []).filter((s) => s.type !== "airport").map((s) => s.outOfTown ? "OOT" : s.locationName || "—").join(" → ") || "—"}>
        {(() => {
          const stops = (job.stops || []).filter((s) => s.type !== "airport");
          if (stops.length === 0) return <span style={{ color: "#bbb" }}>—</span>;
          return stops.map((s, i) => (
            <span key={i}>
              {i > 0 && <span style={{ color: "#ccc" }}> · </span>}
              {s.outOfTown ? (
                <span style={{
                  background: "#fff7e0", color: "#a36b1d",
                  border: "1px solid #f0c878", borderRadius: 8,
                  padding: "0 5px", fontSize: 10, fontWeight: 700,
                }}>OOT</span>
              ) : (
                <span>{s.locationName || "—"}</span>
              )}
            </span>
          ));
        })()}
      </span>
      {/* Address line under the stop names (same as Ready to bill). */}
      {(() => {
        const addrs = (job.stops || [])
          .filter((s) => s.type !== "airport" && !s.outOfTown && s.address)
          .map((s) => s.address);
        return addrs.length > 0 && (
          <span title={addrs.join(" · ")} style={{
            display: "block", fontSize: 10, color: "#999", marginTop: 1,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{addrs.join(" · ")}</span>
        );
      })()}
      </div>
      <div style={{ lineHeight: 1.3 }}>
        {formatScheduledShort(job.scheduledDate) ? (
          <div style={{ fontSize: 12, fontWeight: 600, color: "#111" }}>
            📅 {formatScheduledShort(job.scheduledDate)}
            {job.scheduledTime && (
              <span style={{ marginLeft: 4, fontWeight: 400, color: "#555" }}>{job.scheduledTime}</span>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: "#c9a25a", fontStyle: "italic" }}>Not scheduled</div>
        )}
        <div style={{ fontSize: 10, color: "#aaa" }}>created {formatDate(job.createdAt)}</div>
        {/* POD indicator for waybill-billed (Ferrari/Executive) rows —
            confirms a dropped document landed, or hints one is needed. */}
        {onPodDrop && (
          hasPod ? (
            <div style={{ fontSize: 9, color: "#0a7a4a", fontWeight: 700 }}>📄 POD on file</div>
          ) : (
            <div style={{ fontSize: 9, color: "#bbb" }}>📄 drop waybill here</div>
          )
        )}
      </div>
      <span className={`chip ${st.chip}`}>{st.label}</span>
      <div className="print-actions-col" style={{ display: "flex", gap: 4, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
        {onReadyToBill && ["intake", "pool", "grabbed", "confirmed", "inProgress"].includes(job.status) && (
          <button className="btn btn-sm btn-blue" title="Mark complete and send to Ready to bill"
            onClick={() => onReadyToBill(job)}>→ Bill</button>
        )}
        {job.status === "intake" && (
          <button className="btn btn-sm btn-green" onClick={() => onRelease(job.id)}>Release</button>
        )}
        {["intake","pool"].includes(job.status) && (
          <button className="btn btn-sm" style={{ color: "#a32d2d", borderColor: "#a32d2d" }}
            onClick={() => onCancel(job.id)}>Cancel</button>
        )}
        {job.status === "grabbed" && (
          <button className="btn btn-sm btn-blue" onClick={() => onConfirm(job.id)}>Confirm guard</button>
        )}
        {job.status === "inProgress" && (
          <button className="btn btn-sm" style={{ color: "#085041", borderColor: "#085041" }}>View live</button>
        )}
      </div>
    </div>
  );
}

// ── Phone layout ─────────────────────────────────────────────
// Under PHONE_WIDTH the table becomes one card per job, grouped under day
// headers. Same actions as the table rows (no new behavior); Cancel and
// "Open job" sit in a ⋯ menu so a stray tap next to Bill can't cancel.
const PHONE_WIDTH = 700;

// "Ferrari Express Inc." -> "Ferrari"; "Dana Augustine Inc." -> "Dana Augustine".
function shortBroker(name) {
  return String(name || "").replace(/,?\s+(express\s+)?(inc\.?|llc|corp\.?)$/i, "").trim();
}

function dayHeading(ymd, today) {
  if (!ymd) return "Not scheduled";
  const [y, m, d] = ymd.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const [ty, tm, td] = today.split("-").map(Number);
  const diff = Math.round((date - new Date(ty, tm - 1, td)) / 86400000);
  const label = date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  if (diff === 0) return `Today · ${label}`;
  if (diff === 1) return `Tomorrow · ${label}`;
  return diff < 0 ? `${label} · overdue` : label;
}

function PhoneJobList({ jobs, onSelect, onRelease, onCancel, onConfirm, onReadyToBill, onPodDrop, dispatcherFor }) {
  const today = todayYmd();
  // Date order with day headers, whatever the table's sort column is.
  const ordered = jobs.slice().sort((a, b) =>
    (a.scheduledDate || "9999").localeCompare(b.scheduledDate || "9999")
    || (a.scheduledTime || "99:99").localeCompare(b.scheduledTime || "99:99"));
  const days = [];
  for (const j of ordered) {
    const key = j.scheduledDate || "";
    if (!days.length || days[days.length - 1].key !== key) days.push({ key, jobs: [] });
    days[days.length - 1].jobs.push(j);
  }
  return (
    <div className="screen-only">
      {days.map((d) => (
        <div key={d.key || "none"}>
          <div style={{ padding: "12px 4px 6px", fontSize: 11, fontWeight: 800, color: "#5b6675", letterSpacing: "0.04em", textTransform: "uppercase" }}>
            {dayHeading(d.key, today)}
          </div>
          {d.jobs.map((job) => (
            <PhoneJobCard key={job.id} job={job} onSelect={onSelect} onRelease={onRelease}
              onCancel={onCancel} onConfirm={onConfirm}
              onReadyToBill={onReadyToBill(job)} onPodDrop={onPodDrop(job)}
              dispatcherName={dispatcherFor(job)} />
          ))}
        </div>
      ))}
    </div>
  );
}

function PhoneJobCard({ job, onSelect, onRelease, onCancel, onConfirm, onReadyToBill, onPodDrop, dispatcherName }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const fileRef = useRef(null);
  const fix = deriveJobStatusFix(job);
  const effectiveStatus = fix?.status || job.status;
  const st = STATUS_LABELS[effectiveStatus] || { label: effectiveStatus, chip: "chip-gray" };
  const stops = (job.stops || []).filter((s) => s.type !== "airport");
  const broker = shortBroker(job.brokerName);
  const dispatcher = shortBroker(dispatcherName || job.dispatcherName);
  const showDispatcher = dispatcher && !broker.toLowerCase().startsWith(dispatcher.toLowerCase());
  const required = Number(job.numGuards) || (job.guardAssignments?.length || 1);
  const assigned = job.guardAssignments || [];
  const hasPod = (job.stops || []).some((s) => s.podPhotoUrl) || (job.packages || []).some((p) => p.podPhotoUrl);
  const canBill = onReadyToBill && ["intake", "pool", "grabbed", "confirmed", "inProgress"].includes(job.status);
  const canCancel = ["intake", "pool"].includes(job.status);
  const btn = { flex: 1, minHeight: 40, fontSize: 13, fontWeight: 700 };
  const needsPod = onPodDrop && !hasPod;
  // Room for two buttons beside ⋯; Add POD goes in the menu when both are taken.
  const mainCount = (canBill ? 1 : 0) + (job.status === "intake" || job.status === "grabbed" ? 1 : 0);
  const podInMenu = needsPod && mainCount >= 2;
  const pickPod = () => fileRef.current?.click();
  return (
    <div onClick={() => onSelect(job.id)} style={{
      background: "#fff", border: "1px solid #e3e6ea", borderRadius: 12,
      padding: "12px 14px", marginBottom: 10, cursor: "pointer", position: "relative",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontWeight: 800, color: "#185FA5", fontSize: 15, minWidth: 0, overflowWrap: "anywhere" }}>
          {jobDisplayNumber(job) || "—"}
          {job.subJobLabel && <span style={{ marginLeft: 4, fontWeight: 400, color: "#888", fontSize: 12 }}>({job.subJobLabel})</span>}
        </span>
        <span className={`chip ${st.chip}`} style={{ flexShrink: 0 }}>{st.label}</span>
      </div>
      <div style={{ fontSize: 12, color: "#5b6675", marginTop: 3 }}>
        <strong style={{ color: "#333" }}>{JOB_TYPE_LABELS[job.jobType] || job.jobType || "—"}</strong>
        {job.scheduledTime && ` · ${job.scheduledTime}`}
        {broker && ` · ${broker}`}
        {showDispatcher && ` · ${dispatcher}`}
        {job.salesRepName && ` · 👤 ${job.salesRepName}`}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8 }}>
        {stops.length === 0 ? "—" : stops.map((s) => (s.outOfTown ? "Out of town" : s.locationName || "—")).join(" → ")}
      </div>
      {stops.length === 1 && stops[0].address && !stops[0].outOfTown && (
        <div style={{ fontSize: 11.5, color: "#8a94a1", marginTop: 1 }}>{stops[0].address}</div>
      )}
      <div style={{ fontSize: 12, marginTop: 6, color: assigned.length >= required ? "#0a7a4a" : "#a36b1d" }}>
        {assigned.length}/{required} guard{required !== 1 ? "s" : ""} assigned
        {assigned.length > 0
          ? ` · ${assigned.map((g) => shortName(g.guardName)).join(", ")}`
          : " · open"}
      </div>
      {onPodDrop && hasPod && <div style={{ fontSize: 11, color: "#0a7a4a", fontWeight: 700, marginTop: 4 }}>📄 POD on file</div>}

      <div style={{ display: "flex", gap: 8, marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
        {canBill && <button className="btn btn-blue" style={btn} onClick={() => onReadyToBill(job)}>→ Bill</button>}
        {job.status === "intake" && <button className="btn btn-green" style={btn} onClick={() => onRelease(job.id)}>Release</button>}
        {job.status === "grabbed" && <button className="btn btn-blue" style={btn} onClick={() => onConfirm(job.id)}>Confirm guard</button>}
        {needsPod && !podInMenu && <button className="btn" style={btn} onClick={pickPod}>📷 Add POD</button>}
        {needsPod && (
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={(e) => { onPodDrop(job, e.target.files); e.target.value = ""; }} />
        )}
        {!canBill && job.status !== "intake" && job.status !== "grabbed" && (
          <button className="btn" style={btn} onClick={() => onSelect(job.id)}>Open job</button>
        )}
        <button className="btn" style={{ ...btn, flex: "0 0 44px" }} aria-label="More actions"
          onClick={() => setMenuOpen((o) => !o)}>⋯</button>
      </div>
      {menuOpen && (
        <div onClick={(e) => e.stopPropagation()} style={{
          position: "absolute", right: 14, bottom: 58, zIndex: 20, background: "#fff",
          border: "1px solid #dde2e8", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.15)",
          minWidth: 200, overflow: "hidden", fontSize: 14,
        }}>
          <div style={{ padding: "12px 14px", borderBottom: "1px solid #f0f2f5" }}
            onClick={() => { setMenuOpen(false); onSelect(job.id); }}>Open job (edit, reschedule, guards)</div>
          {podInMenu && (
            <div style={{ padding: "12px 14px", borderBottom: "1px solid #f0f2f5" }}
              onClick={() => { setMenuOpen(false); pickPod(); }}>📷 Add POD</div>
          )}
          {canCancel && (
            <div style={{ padding: "12px 14px", color: "#b3261e", fontWeight: 700 }}
              onClick={() => { setMenuOpen(false); onCancel(job.id); }}>Cancel job</div>
          )}
          <div style={{ padding: "12px 14px", color: "#888" }} onClick={() => setMenuOpen(false)}>Close</div>
        </div>
      )}
    </div>
  );
}
